// =====================================================
// SALES EXECUTIVE API CLIENT
// Thin wrapper around the /api/sales endpoints, mirroring services/activityApi.js:
// same BASE_URL rule, same per-call authenticated axios instance (so it always
// picks up the freshest token), same "export one object per audience" shape.
//
// Unlike the activity client, errors are NOT swallowed here — the Sales
// Executive screens must surface a real failure (a suspended account, a
// removed role, a bad email) rather than silently render zeros.
// =====================================================

import axios from "axios";

export const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const token = () => localStorage.getItem("token");

function api() {
  return axios.create({
    baseURL: BASE_URL,
    headers: { Authorization: `Bearer ${token()}` },
  });
}

// Pull the server's message out of an axios error so every screen can show the
// real reason instead of a generic "something went wrong".
export function errorMessage(err, fallback = "Something went wrong") {
  return err?.response?.data?.message || err?.message || fallback;
}

// ── SALES EXECUTIVE (own data) ──
export const salesExecutive = {
  dashboard: () => api().get("/api/sales/me/dashboard").then((r) => r.data),
  incentives: (params) =>
    api().get("/api/sales/me/incentives", { params }).then((r) => r.data),
};

// ── ADMIN ──
export const salesAdmin = {
  overview: () => api().get("/api/sales/admin/overview").then((r) => r.data),

  listExecutives: () =>
    api().get("/api/sales/admin/executives").then((r) => r.data),

  createExecutive: (payload) =>
    api().post("/api/sales/admin/executives", payload).then((r) => r.data),

  setStatus: (id, status) =>
    api()
      .patch(`/api/sales/admin/executives/${id}/status`, { status })
      .then((r) => r.data),

  removeExecutive: (id) =>
    api().delete(`/api/sales/admin/executives/${id}`).then((r) => r.data),

  referralStats: (id, params) =>
    api()
      .get(`/api/sales/admin/executives/${id}/referrals`, { params })
      .then((r) => r.data),

  listIncentives: (params) =>
    api().get("/api/sales/admin/incentives", { params }).then((r) => r.data),

  reviewIncentive: (id, action, reason) =>
    api()
      .patch(`/api/sales/admin/incentives/${id}`, { action, reason })
      .then((r) => r.data),

  fraudReports: () => api().get("/api/sales/admin/fraud").then((r) => r.data),

  // ── POINT 10 — stored fraud data + actions (additive) ──
  fraudAlerts: (params) =>
    api().get("/api/sales/admin/fraud/alerts", { params }).then((r) => r.data),
  fraudUserReports: (params) =>
    api().get("/api/sales/admin/fraud/reports", { params }).then((r) => r.data),
  repeatOffenders: () =>
    api().get("/api/sales/admin/fraud/repeat-offenders").then((r) => r.data),
  fraudUserDetail: (id) =>
    api().get(`/api/sales/admin/fraud/user/${id}`).then((r) => r.data),
  confirmFraud: (id) =>
    api().post(`/api/sales/admin/fraud/user/${id}/confirm`).then((r) => r.data),
  markFalsePositive: (id) =>
    api().post(`/api/sales/admin/fraud/user/${id}/false-positive`).then((r) => r.data),
  listDisposableDomains: () =>
    api().get("/api/sales/admin/security/disposable-domains").then((r) => r.data),
  addDisposableDomain: (domain, note) =>
    api()
      .post("/api/sales/admin/security/disposable-domains", { domain, note })
      .then((r) => r.data),

  // ── CHANGE REQUEST 01 — blocked devices + whitelist override ──
  listBlockedDevices: (params) =>
    api().get("/api/sales/admin/security/blocked-devices", { params }).then((r) => r.data),
  listDeviceWhitelist: () =>
    api().get("/api/sales/admin/security/device-whitelist").then((r) => r.data),
  whitelistDevice: (deviceHash, reason) =>
    api()
      .post("/api/sales/admin/security/device-whitelist", { deviceHash, reason })
      .then((r) => r.data),
  unwhitelistDevice: (deviceHash, reason) =>
    api()
      .delete(`/api/sales/admin/security/device-whitelist/${encodeURIComponent(deviceHash)}`, {
        params: { reason },
      })
      .then((r) => r.data),

  // ── POINT 11 — T&C admin visibility ──
  termsAcceptances: (params) =>
    api().get("/api/sales/admin/terms/acceptances", { params }).then((r) => r.data),
  termsVersions: () =>
    api().get("/api/sales/admin/terms/versions").then((r) => r.data),
  publishTerms: (payload) =>
    api().post("/api/sales/admin/terms", payload).then((r) => r.data),
};

// ── SALES EXECUTIVE — T&C (own) ──
export const salesTerms = {
  current: () => api().get("/api/sales/me/terms").then((r) => r.data),
  accept: (version, deviceHash) =>
    api().post("/api/sales/me/terms/accept", { version, deviceHash }).then((r) => r.data),
};

// ── REFERRAL CODE VALIDATION (public — used by the registration form) ──
export function validateReferralCode(code) {
  return axios
    .get(`${BASE_URL}/api/referral/validate/${encodeURIComponent(code)}`)
    .then((r) => r.data);
}

export default salesExecutive;
